import {
	Delete as DeleteIcon,
	Edit as EditIcon,
	ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import {
	Collapse,
	IconButton,
	Paper,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography,
} from '@mui/material';
import React, { useState } from 'react';
import { AutofillCallResponse } from '../../api/integrations';
import {
	AUTOFILL_PLUGIN_LABEL,
	AUTOFILL_SEQUENCE_OPERATION_LABEL,
	AUTOFILL_SEQUENCE_OPERATION_TYPE_LABEL,
	AUTOFILL_SEQUENCE_PRODUCT_LABEL,
	AUTOFILL_SEQUENCE_TABLE_ACTIONS_LABEL,
	AUTOFILL_SEQUENCE_TABLE_CALL_SEQUENCE_LABEL,
} from '../../data/constants';
import { getAutofillOperationName } from '../../utils/getAutofillOperationName';

type Props = {
	autofillSequences: AutofillCallResponse[];
	onEdit: (autofillSequence: AutofillCallResponse) => void;
	onDelete: (autofillSequence: AutofillCallResponse) => void;
};

type TableHeader = {
	label: string;
	align: 'center' | 'inherit' | 'left';
};

type ExpandInfoState = {
	[autofillSequenceName: string]: boolean;
};

// TODO: implement skeleton while loading
export default function AutofillSequencesTable({
	autofillSequences,
	onEdit,
	onDelete,
}: Props) {
	const [expandInfo, setExpandInfo] = useState<ExpandInfoState>({});

	const tableHeaders: TableHeader[] = [
		{
			label: '',
			align: 'inherit',
		},
		{
			label: AUTOFILL_SEQUENCE_PRODUCT_LABEL(),
			align: 'left',
		},
		{
			label: AUTOFILL_SEQUENCE_OPERATION_LABEL(),
			align: 'left',
		},
		{
			label: AUTOFILL_SEQUENCE_OPERATION_TYPE_LABEL(),
			align: 'left',
		},
		{
			label: AUTOFILL_SEQUENCE_TABLE_ACTIONS_LABEL(),
			align: 'center',
		},
	];
	const renderTableHeader = () => {
		return (
			<TableHead id='DataAutofillsPanel__AutofillSequencesTableHead'>
				<TableRow id='DataAutofillsPanel__AutofillSequencesTableHeadRow'>
					{tableHeaders.map((header, index) => (
						<TableCell
							key={`AutofillSequencesTable__TableHeaderCell-${index}`}
							sx={{ backgroundColor: 'white.lightest' }}
							align={header.align}
						>
							<Typography fontWeight='bold'>{header.label}</Typography>
						</TableCell>
					))}
				</TableRow>
			</TableHead>
		);
	};

	const renderCollapseSection = (autofillSequence: AutofillCallResponse) => {
		return (
			<Stack
				direction='column'
				spacing={1}
				paddingBottom={2}
				paddingLeft={1}
				paddingTop={1}
			>
				<Typography variant='body2' fontWeight='bold'>
					{AUTOFILL_SEQUENCE_TABLE_CALL_SEQUENCE_LABEL()}
				</Typography>
				{autofillSequence.callSequence.map((call, index) => (
					<Stack key={autofillSequence.locator} direction='row'>
						<Typography variant='body2' color='body2' sx={{ mr: 1 }}>
							{index + 1}.
						</Typography>
						<Typography
							variant='body2'
							color='body2'
							sx={{ mr: 1, wordBreak: 'break-all' }}
						>
							{call === 'internal' ? AUTOFILL_PLUGIN_LABEL() : call}
						</Typography>
					</Stack>
				))}
			</Stack>
		);
	};

	const renderTableBody = () => {
		return (
			<TableBody id='DataAutofillsPanel__AutofillSequencesTableBody'>
				{autofillSequences?.map((autofillSequence, index) => {
					const autofillOperation = autofillSequence.operation.split('.');
					return (
						<React.Fragment key={autofillSequence.locator}>
							<TableRow
								sx={{
									'& td': { border: 0, paddingY: 0 },
								}}
							>
								<TableCell
									align='center'
									sx={{
										paddingX: 1,
										paddingY: 0,
									}}
								>
									<IconButton
										onClick={() => {
											setExpandInfo({
												...expandInfo,
												[autofillSequence.locator]: !(
													expandInfo[autofillSequence.locator] ?? false
												),
											});
										}}
										id={`AutofillSequencesTableBody__${autofillSequence.locator}-CollapseButton`}
									>
										<ExpandMoreIcon
											sx={{
												transform: expandInfo[autofillSequence.locator]
													? 'rotate(180deg)'
													: 'rotate(0deg)',
												transition:
													'transform 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
											}}
										/>
									</IconButton>
								</TableCell>
								<TableCell>
									{getAutofillOperationName(autofillOperation[0])}
								</TableCell>
								<TableCell>
									{getAutofillOperationName(autofillOperation[1])}
								</TableCell>
								<TableCell>
									{getAutofillOperationName(autofillOperation[2])}
								</TableCell>
								<TableCell align='center'>
									<IconButton
										color='primary'
										onClick={() => onEdit(autofillSequence)}
									>
										<EditIcon fontSize='small' />
									</IconButton>
									<IconButton
										color='primary'
										onClick={() => {
											onDelete(autofillSequence);
										}}
									>
										<DeleteIcon fontSize='small' />
									</IconButton>
								</TableCell>
							</TableRow>
							<TableRow>
								<TableCell
									colSpan={tableHeaders.length}
									sx={{
										paddingTop: 0,
										paddingBottom: 0,
									}}
								>
									<Collapse
										in={expandInfo[autofillSequence.locator]}
										timeout='auto'
										unmountOnExit
									>
										{renderCollapseSection(autofillSequence)}
									</Collapse>
								</TableCell>
							</TableRow>
						</React.Fragment>
					);
				})}
			</TableBody>
		);
	};
	return (
		<Paper sx={{ flex: 1, overflowY: 'auto', borderRadius: 2 }}>
			<TableContainer
				sx={{ maxHeight: '100%' }}
				id='AutofillPanel__AutofillSequencesTableContainer'
			>
				<Table
					stickyHeader
					size='small'
					id='AutofillPanel__AutofillSequencesTable'
				>
					{renderTableHeader()}
					{renderTableBody()}
				</Table>
			</TableContainer>
		</Paper>
	);
}
